<template>
  <slot v-if="disabled" name="trigger" />

  <DialogRoot v-else v-model:open="isOpen" modal>
    <DialogTrigger asChild>
      <slot name="trigger" />
    </DialogTrigger>

    <DialogPortal>
      <Transition name="overlay">
        <DialogOverlay class="PanelSlideOverlay" />
      </Transition>

      <Transition :name="`slide-${side}`">
        <DialogContent
          ref="panelContent"
          class="PanelSlide"
          :class="[`PanelSlide--${side}`, { 'fullscreen': fullscreenOnMobile }]"
          v-bind="$attrs"
          @open-auto-focus="focusPanelContainer"
        >
          <DialogClose class="PanelSlide-close" aria-label="Close panel">
            <IconButton icon="window-close" size="lg" ariaLabel="Close panel" />
          </DialogClose>

          <slot />
        </DialogContent>
      </Transition>
    </DialogPortal>
  </DialogRoot>
</template>

<script setup lang="ts">
import { useTemplateRef } from 'vue';
import {
  DialogRoot,
  DialogTrigger,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogClose,
} from 'reka-ui';
import { whenever } from '@vueuse/core';
import IconButton from '../../atoms/IconButton/IconButton.vue';
import type { PanelSlideProps } from './PanelSlide.model';

withDefaults(defineProps<PanelSlideProps>(), {
  side: 'start',
  disabled: false,
  fullscreenOnMobile: false,
});

defineOptions({ inheritAttrs: false });

const panelContent = useTemplateRef('panelContent');

const isOpen = defineModel<boolean>({ default: false });

whenever(isOpen, () => {
  // Fixes pointer-events being disabled on body which affects 3rd party modals
  // https://github.com/unovue/reka-ui/issues/1540
  setTimeout(() => {
    document.body.style.pointerEvents = '';
  });
});

/** Prevent focusing the first focusable element inside the panel */
function focusPanelContainer(event: Event) {
  event.preventDefault();
  panelContent.value?.$el.focus();
}

/** Scroll panel's content to top */
function scrollToTop() {
  if (panelContent.value?.$el) {
    panelContent.value.$el.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

defineExpose({
  scrollToTop,
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/mixins';
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  .PanelSlideOverlay {
    @include mixins.set-overlay;
  }

  .PanelSlide {
    position: fixed;
    top: 0;
    bottom: 0;
    isolation: isolate;

    background-color: vars.$organisms-panel-dropdown-bg-color;
    box-shadow: vars.$shadow-lg;

    min-width: 300px;
    max-width: 100vw;
    padding: 20px;

    overflow-y: auto;

    &.fullscreen {
      @include responsive.mobile {
        width: 100vw;
      }
    }

    &--start {
      inset-inline-start: 0;
    }

    &--end {
      inset-inline-end: 0;
    }

    &-close {
      position: absolute;
      inset-block-start: 20px;
      inset-inline-end: 20px;

      color: vars.$organisms-panel-dropdown-icon-close-color;
    }
  }
}
</style>
